import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, Check, CalendarClock, MessageCircle, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const NotificationItem = ({ item, surface, onOpen, onRead, busy }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.slice(0, 2);
  const data = { ...item.data, job: item.data[`job_${lang}`] || item.data.job || t('notifications.jobFallback'),
    candidate: item.data.candidate || t('notifications.candidateFallback'), company: item.data.company || t('notifications.companyFallback') };

  const isPioneer = item.kind === 'prospect_activated';
  const Icon = isPioneer ? Trophy
    : item.kind === 'application_new' ? Briefcase
    : item.kind.startsWith('interview_') ? CalendarClock
    : MessageCircle;

  const id = `${surface}-notification-${item.id}`;
  const rowBg = item.read_at
    ? 'bg-white'
    : isPioneer
      ? 'bg-emerald-50/80 border-l-4 border-emerald-500'
      : 'bg-blue-50/70';
  const iconBg = isPioneer
    ? 'bg-emerald-100 border-emerald-200 text-emerald-700'
    : 'bg-white border-blue-100 text-blue-700';

  return (
    <li className={`flex items-start gap-3 p-4 border-b last:border-b-0 transition-colors ${rowBg}`} data-testid={id}>
      <span className={`h-9 w-9 shrink-0 rounded-lg border flex items-center justify-center ${iconBg}`} aria-hidden="true">
        <Icon className="h-4 w-4" />
      </span>
      <Link to={item.href} onClick={(e) => { e.preventDefault(); onOpen(item); }}
        className="min-w-0 flex-1 rounded-sm focus-visible:outline-blue-600 hover:underline decoration-blue-200"
        data-testid={`${id}-open`}>
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900" data-testid={`${id}-title`}>
          {!item.read_at && <span className={`h-2 w-2 shrink-0 rounded-full ${isPioneer ? 'bg-emerald-600' : 'bg-blue-600'}`}
            aria-label={t('notifications.unread')} data-testid={`${id}-unread`} />}
          <span className="break-words">{t(`notifications.events.${item.kind}.title`, { defaultValue: t('notifications.update') })}</span>
        </span>
        <p className="text-sm text-slate-600 mt-1 break-words [overflow-wrap:anywhere]" data-testid={`${id}-body`}>
          {t(`notifications.events.${item.kind}.body`, { ...data, defaultValue: data.job })}
        </p>
        <time dateTime={item.created_at} className="block mt-2 text-xs text-slate-500" data-testid={`${id}-date`}>
          {new Date(item.created_at).toLocaleString({ pt: 'pt-PT', en: 'en-GB', fr: 'fr-FR' }[lang], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </time>
      </Link>
      {!item.read_at && (
        <Button size="icon" variant="ghost"
          className={`h-8 w-8 shrink-0 ${isPioneer ? 'text-emerald-700' : 'text-blue-700'}`}
          disabled={busy} onClick={() => onRead(item)}
          title={t('notifications.markRead')} aria-label={t('notifications.markRead')}
          data-testid={`${id}-read`}>
          <Check className="h-4 w-4" />
        </Button>
      )}
    </li>
  );
};